/**
 * Add or edit a connection in one simple form: platform type (add only), name,
 * base URL, then the connector's own fields. Editing can replace the secret;
 * blank secret fields keep what is saved. Adding, or saving a new address or
 * secret, tests the connection and shows the outcome here; so does "Test
 * connection". An edit opened on a failed connection starts with that failure.
 * An agent platform also says where its runs' traces land ("Traces in").
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { Select } from "@/components/ui/select";
import { QueryState, errorText } from "@/components/ui/states";
import { useConnectors } from "@/features/connectors/hooks";
import type { Connector } from "@/features/connectors/types";
import { useUiStore } from "@/stores/ui-store";
import {
  ConnectionFields,
  draftOf,
  draftReady,
  emptyDraft,
  secretOf,
  type ConnectionDraft,
} from "./connection-fields";
import { CheckResult, lastCheck, type CheckOutcome } from "./check-result";
import { useCheckConnection, useCreateConnection, useUpdateConnection } from "./hooks";
import { TracesInField } from "./traces-in-field";
import type { Connection } from "./types";

function Form({
  connectors,
  saved,
  onClose,
}: {
  connectors: Connector[];
  saved: Connection | undefined;
  onClose: () => void;
}) {
  const [type, setType] = useState(saved?.connector_type ?? connectors[0]?.type ?? "");
  const [draft, setDraft] = useState<ConnectionDraft>(() => (saved ? draftOf(saved) : emptyDraft()));
  const [tracesIn, setTracesIn] = useState(saved?.traces_connection_id ?? "");
  /** The connection as last saved here: later saves edit it, and its test shows below. */
  const [created, setCreated] = useState<Connection>();
  const current = created ?? saved;
  const [result, setResult] = useState<CheckOutcome | null>(() => lastCheck(saved));
  /** Saved in this dialog: closing then loses nothing, so it says Done. */
  const [stored, setStored] = useState(false);
  const create = useCreateConnection();
  const update = useUpdateConnection(current?.id ?? "");
  const check = useCheckConnection();
  const notify = useUiStore((s) => s.notify);
  const connector = connectors.find((c) => c.type === type);
  const pending = create.isPending || update.isPending || check.isPending;

  const test = (c: Connection) => {
    setResult(null);
    check.mutate(c.id, {
      onSuccess: (r) => setResult(r),
      onError: (e) => setResult({ ok: false, message: errorText(e, "Could not test the connection.") }),
    });
  };

  const submit = () => {
    if (!connector) return;
    const secret = secretOf(connector, draft, current);
    const tracesChanged = connector.kind === "agent" && tracesIn !== (current?.traces_connection_id ?? "");
    const traces = tracesChanged ? { traces_connection_id: tracesIn || null } : {};
    const body = { name: draft.name.trim(), base_url: draft.base_url.trim(), ...traces };
    const fail = (e: unknown) => notify(errorText(e, "Could not save the connection."), "error");
    // Once saved, the typed secret leaves the form and the mutation: a later Save never resends it.
    const keep = (c: Connection) => {
      setCreated(c);
      setDraft(draftOf(c));
      create.reset();
      update.reset();
    };
    if (!current) {
      const onSuccess = (c: Connection) => (keep(c), setStored(true), test(c));
      create.mutate({ connector_type: type, ...body, secret }, { onSuccess, onError: fail });
      return;
    }
    // A new address or secret is tested at once; a new name alone just closes.
    const access = Object.keys(secret).length > 0 || body.base_url !== current.base_url;
    const onSuccess = (c: Connection) => {
      keep(c);
      notify(`Saved ${c.name}.`);
      if (!access) return onClose();
      setStored(true);
      test(c);
    };
    update.mutate(Object.keys(secret).length ? { ...body, secret } : body, { onSuccess, onError: fail });
  };

  return (
    <Dialog
      title={saved ? `Edit ${saved.name}` : "Add connection"}
      subtitle="Where agents run or traces come from, and how the workbench signs in."
      onClose={onClose}
      width={540}
      footer={
        <>
          {current && (
            <Button disabled={pending} onClick={() => test(current)}>
              Test connection
            </Button>
          )}
          <span className="flex-1" />
          <Button onClick={onClose}>{stored ? "Done" : "Cancel"}</Button>
          <Button
            variant="primary"
            disabled={!connector || !draftReady(connector, draft, current) || pending}
            onClick={submit}
          >
            {current ? "Save" : "Add connection"}
          </Button>
        </>
      }
    >
      {!saved && (
        <Field label="Platform" required htmlFor="connection-type">
          <Select
            id="connection-type"
            value={type}
            disabled={!!created}
            onChange={(t) => (setType(t), setDraft({ ...draft, values: {} }))}
            options={connectors.map((c) => ({ value: c.type, label: c.label }))}
          />
        </Field>
      )}
      {connector && (
        <ConnectionFields
          key={type}
          connector={connector}
          draft={draft}
          saved={current}
          onChange={setDraft}
        />
      )}
      {connector?.kind === "agent" && (
        <TracesInField connectors={connectors} value={tracesIn} onChange={setTracesIn} />
      )}
      <CheckResult checking={check.isPending} result={result} />
    </Dialog>
  );
}

export function ConnectionDialog({ connection, onClose }: { connection?: Connection; onClose: () => void }) {
  const connectors = useConnectors();
  return (
    <QueryState query={connectors} what="platforms">
      <Form connectors={connectors.data ?? []} saved={connection} onClose={onClose} />
    </QueryState>
  );
}
